/**
 * Browser-side session plumbing for HttpOnly Supabase auth cookies
 * (pentest retest #817, Aikido #583).
 *
 * The `sb-<ref>-auth-token` cookies are HttpOnly, so the browser client
 * cannot read them. Instead:
 *   - `createServerSessionStorage` keeps the session in memory, loads it
 *     from `GET /api/auth/session` on first use, and hands any real
 *     refresh token it is given (hash-fragment sign-ins) to the server.
 *   - `createProxiedAuthFetch` sends every Supabase Auth request through
 *     `/api/auth/gotrue`, which sets the cookies and returns
 *     `REFRESH_TOKEN_PLACEHOLDER` in place of the refresh token.
 *
 * The refresh token therefore stays server-side; only the short-lived
 * access token is held in browser memory.
 */

export const REFRESH_TOKEN_PLACEHOLDER = 'server-managed';

export const GOTRUE_PROXY_PATH = '/api/auth/gotrue';
export const SESSION_PATH = '/api/auth/session';
export const CODE_VERIFIER_PATH = '/api/auth/session/code-verifier';

/** How long a "no session" answer from the server is reused. */
const EMPTY_SESSION_CACHE_MS = 1000;

type FetchLike = typeof fetch;

const defaultFetch: FetchLike = (...args) => fetch(...args);

export function createProxiedAuthFetch(
  supabaseUrl: string,
  baseFetch: FetchLike = defaultFetch,
): FetchLike {
  const authPrefix = `${supabaseUrl.replace(/\/+$/, '')}/auth/v1/`;
  return (input, init) => {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (!url.startsWith(authPrefix)) return baseFetch(input, init);
    const target = `${GOTRUE_PROXY_PATH}/${url.slice(authPrefix.length)}`;
    if (typeof input !== 'string' && !(input instanceof URL)) {
      const absolute = typeof location !== 'undefined' ? new URL(target, location.href).href : target;
      return baseFetch(new Request(absolute, input), init);
    }
    return baseFetch(target, { ...init, credentials: 'same-origin' });
  };
}

interface SessionStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

function jsonRequest(method: string, body?: unknown): RequestInit {
  return {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  };
}

export function createServerSessionStorage(
  storageKey: string,
  baseFetch: FetchLike = defaultFetch,
): SessionStorage {
  const memory = new Map<string, string>();
  const verifierKey = `${storageKey}-code-verifier`;
  let version = 0;
  let emptyUntil = 0;
  let inflight: Promise<string | null> | null = null;

  const loadSession = (): Promise<string | null> => {
    if (typeof window === 'undefined' || Date.now() < emptyUntil) {
      return Promise.resolve(null);
    }
    if (inflight) return inflight;
    const startedAt = version;
    inflight = (async () => {
      let value: string | null = null;
      try {
        const res = await baseFetch(SESSION_PATH, jsonRequest('GET'));
        if (res.ok) {
          const body = (await res.json()) as { session?: unknown };
          value = body?.session ? JSON.stringify(body.session) : null;
        }
      } catch {
        value = null;
      }
      if (version !== startedAt) return memory.get(storageKey) ?? null;
      if (value) memory.set(storageKey, value);
      else emptyUntil = Date.now() + EMPTY_SESSION_CACHE_MS;
      return value;
    })().finally(() => {
      inflight = null;
    });
    return inflight;
  };

  const persistSession = async (value: string): Promise<string> => {
    const parsed = JSON.parse(value) as { access_token?: string; refresh_token?: string };
    if (!parsed.refresh_token || parsed.refresh_token === REFRESH_TOKEN_PLACEHOLDER) {
      return value;
    }
    const res = await baseFetch(
      SESSION_PATH,
      jsonRequest('POST', {
        access_token: parsed.access_token,
        refresh_token: parsed.refresh_token,
      }),
    );
    if (!res.ok) throw new Error('Could not establish the session.');
    return JSON.stringify({ ...parsed, refresh_token: REFRESH_TOKEN_PLACEHOLDER });
  };

  return {
    async getItem(key) {
      if (memory.has(key)) return memory.get(key) ?? null;
      return key === storageKey ? loadSession() : null;
    },
    async setItem(key, value) {
      if (key === storageKey) {
        version += 1;
        emptyUntil = 0;
        memory.set(key, await persistSession(value));
        return;
      }
      memory.set(key, value);
      if (key === verifierKey) {
        const res = await baseFetch(CODE_VERIFIER_PATH, jsonRequest('POST', { value }));
        if (!res.ok) throw new Error('Could not start the sign-in.');
      }
    },
    async removeItem(key) {
      memory.delete(key);
      if (key !== storageKey && key !== verifierKey) return;
      if (key === storageKey) version += 1;
      try {
        await baseFetch(key === storageKey ? SESSION_PATH : CODE_VERIFIER_PATH, jsonRequest('DELETE'));
      } catch {
        // Best effort: the server clears the cookies on sign-out as well.
      }
    },
  };
}
